/**
 * Face-to-Face Conversation Tutorial Page (Both mode, other side beside me,
 * desktop app + KizunaAI)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Face-to-Face Conversation Across Two Languages',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'This guide sets up the Sokuji desktop app for two people at one computer who speak different languages: a visitor at a front desk, a meeting across a table, a talk with a relative. You share one microphone and each wear one earbud. What you say is read to the other person in their language, what they say is read to you in yours, and both sides appear as subtitles. Nothing is sent into a meeting app, and you need no virtual audio cable. It uses KizunaAI (Powered by Soniox; face-to-face needs the Soniox engine). The screenshots are from Sokuji 0.44.0 on Windows: I speak Chinese, the other person speaks English, and we share a pair of earbuds, mine in the left ear and theirs in the right.',
  },
  steps: [
    {
      title: 'Set up Sokuji with KizunaAI',
      content:
        'If you have not done so yet, follow the <a href="/docs/kizuna-ai/quick-start">KizunaAI quick start</a> to install Sokuji and sign in.',
    },
    {
      title: 'Choose “Face-to-face conversation”',
      content:
        'In the setup guide, choose <strong>Face-to-face conversation</strong>. It puts Sokuji in <strong>Both</strong> mode with the other side beside you, and reads both translations aloud. If you have already finished the setup, use the next step instead.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Put the other person beside you',
      content:
        'Click <strong>Both</strong> in the mode switch at the bottom left of the main window; if its panel does not open, click Both again. Under <strong>Other side</strong>, choose <strong>Beside me</strong>. The mode switch now carries a <strong>Face-to-face</strong> tag, and the panel shows what face-to-face uses: your microphone, and where each translation plays.',
      screenshot: '02-other-side.png',
      tip: 'Other’s audio is not needed: the other person speaks into your microphone, and Sokuji captures no system audio. Other side cannot change while a session runs.',
    },
    {
      title: 'Set the two languages',
      content:
        'In Settings, under <strong>Translation Languages</strong>, set <strong>I speak</strong> to your language and <strong>they hear</strong> to the other person’s. The line underneath reads the other way round. Here: I speak Chinese, they hear English, and the line says “They speak English → I hear Chinese”.',
      tip: 'Do not leave I speak on Auto Detect. In Both mode Sokuji has to know your language to translate the other person into it, and Start stays disabled until you choose one.',
    },
    {
      title: 'Choose one microphone for both of you',
      content:
        'Under <strong>Microphone</strong>, pick a microphone that hears you both, such as the built-in microphone of a laptop placed between you, or a desk microphone. The microphone of a headset hears only the person wearing it.',
    },
    {
      title: 'Put in the earbuds and set the ears',
      content:
        'You wear the left earbud, the other person the right one. In the <strong>Speech</strong> block, <strong>Translation the other side hears</strong> plays your translation in the other person’s ear (<strong>right channel</strong>), and <strong>Translation I hear</strong> plays theirs in yours (<strong>left channel</strong>), both on the default playback device. The play button beside each row plays a short tone in that ear. To swap ears, choose the other channel in each row; to give each of you a headset of your own, choose a device in each row. Ears and devices can change during a session too.',
      screenshot: '03-speech.png',
      tip: 'One earbud each, never a speaker: through a speaker the microphone picks up the translation and translates it again. I hear it too and Passthrough are not shown in face-to-face.',
    },
    {
      title: 'Pick the two voices',
      content:
        'Click the voice name under either row, or open <strong>Settings → Advanced → Provider</strong>. <strong>Voice the other side hears</strong> is the voice the other person hears your translation in: a preset or your own cloned voice. <strong>Voice I hear</strong> reads the other person’s translation to you, from the preset voices. By default the two are different voices. The <a href="/docs/kizuna-ai/voices">voices guide</a> has more.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Start and take turns',
      content:
        'Click <strong>Start</strong> and speak in turn. Each sentence appears with its translation and is read into the listener’s ear: yours into the other person’s, theirs into yours. The strip above Start shows each person’s ear, language and device, here <strong>L Chinese · me</strong> and <strong>R English · other person</strong>, and each translated line carries an <strong>L</strong> or <strong>R</strong> tag for the ear it plays in. A line marked with a crossed-out speaker is not played: its translation came out in the speaker’s own language, which can happen when someone mixes both languages, so it is read to no one.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Take turns and pause at the end of each sentence. When you both talk at once, one microphone hears you mixed together.',
      'Keep to your own language. A sentence in the other person’s language may come out translated into your own, and then it is read to no one.',
      'Translation the other side hears and Translation I hear are fixed once a session runs. Stop the session to change them.',
      'Stop the session when the conversation ends: a running session keeps using your balance.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Can we use subtitles only?',
        answer:
          'Yes. Choose Face-to-face conversation, subtitles only in the setup guide, or switch off Translation the other side hears and Translation I hear in the Speech block before you start. You then both read the screen, and in the desktop app the other person can also follow on their phone through caption sharing. You can also switch off just one of the two, for example when the other person would rather read.',
      },
      {
        question: 'What does a face-to-face conversation cost?',
        answer:
          'It always runs as one shared session: Shared session in Both mode is fixed to Enabled. With voice, both halves are spoken, your translation and the other person’s, so it costs more per minute than subtitles only and needs a higher balance to start. The billing guide has the details.',
      },
      {
        question: 'Do I need VB-CABLE for this?',
        answer: 'No. Face-to-face plays both translations in your earbuds and sends nothing into a meeting app.',
      },
      {
        question: 'Which providers can do face-to-face?',
        answer: 'KizunaAI with its Soniox engine, and Soniox with your own API key. Other providers, the Qwen engine included, cannot translate two people at one microphone.',
      },
      {
        question: 'Is there a button to swap the ears?',
        answer: 'No. Choose the left or right channel in each of the two rows of the Speech block, or in the panel that opens from the mode switch.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '两种语言的面对面对话',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      '本指南介绍如何设置 Sokuji 桌面应用，让说不同语言的两个人在同一台电脑前交谈：前台接待来访者、隔着桌子开会、和亲戚聊天。你们共用一个麦克风，每人戴一只耳机。你说的话会用对方的语言读给对方听，对方说的话会用你的语言读给你听，双方的内容也都会以字幕显示。不会向任何会议应用发送内容，也不需要虚拟音频线。本指南使用 KizunaAI（Soniox 驱动；面对面对话需要 Soniox 引擎）。截图来自 Windows 上的 Sokuji 0.44.0：我说中文，对方说英语，我们共用一副耳机，我戴左耳，对方戴右耳。',
  },
  steps: [
    {
      title: '用 KizunaAI 设置 Sokuji',
      content:
        '如果还没有完成，请按照 <a href="/docs/kizuna-ai/quick-start">KizunaAI 快速入门</a>安装 Sokuji 并登录。',
    },
    {
      title: '选择“面对面对话”',
      content:
        '在设置向导中选择<strong>面对面对话</strong>。它会让 Sokuji 进入<strong>两者</strong>模式、对方在身边，并把双方的翻译都朗读出来。如果已经完成设置，请改用下一步。',
      screenshot: '01-scenario.png',
    },
    {
      title: '告诉 Sokuji 对方就在身边',
      content:
        '点击主窗口左下角模式切换中的<strong>两者</strong>；如果它的面板没有打开，请再点击一次“两者”。在<strong>对方在哪里</strong>下选择<strong>就在身边</strong>。模式切换上会出现<strong>面对面</strong>标签，面板中会显示面对面对话用到的东西：你的麦克风，以及每条翻译在哪里播放。',
      screenshot: '02-other-side.png',
      tip: '不需要设置“对方音频”：对方对着你的麦克风说话，Sokuji 不会捕获系统音频。会话进行中无法更改“对方在哪里”。',
    },
    {
      title: '设置两种语言',
      content:
        '在设置的<strong>翻译语言</strong>下，将<strong>我说</strong>设为你的语言，将<strong>对方听到</strong>设为对方的语言。下方那一行显示反方向。此处：我说中文，对方听到英语，下方那一行为“对方说英语 → 我听到中文”。',
      tip: '不要让“我说”停留在“自动检测”。在两者模式下，Sokuji 需要知道你的语言才能把对方的话翻译过来；在你选定语言之前，“开始”按钮会一直不可用。',
    },
    {
      title: '选择一个你们俩共用的麦克风',
      content:
        '在<strong>麦克风</strong>下选择一个能同时听到你们两个人的麦克风，例如放在你们中间的笔记本电脑的内置麦克风，或者桌面麦克风。耳机上的麦克风只能听到戴着它的人。',
    },
    {
      title: '戴上耳机并设置左右耳',
      content:
        '你戴左耳，对方戴右耳。在<strong>语音</strong>部分，<strong>对方听到的翻译</strong>把你的翻译播放到对方的耳朵（<strong>右声道</strong>），<strong>我听到的翻译</strong>把对方的翻译播放到你的耳朵（<strong>左声道</strong>），两者都在默认播放设备上播放。每一行旁边的播放按钮会在那只耳朵里播放一段短提示音。要交换左右耳，请在每一行改选另一个声道；如果你们各用一副耳机，请在每一行分别选择设备。左右耳和设备在会话进行中也可以更改。',
      screenshot: '03-speech.png',
      tip: '每人一只耳机，不要用扬声器：用扬声器播放时，麦克风会拾取译音并再次翻译。面对面对话中不显示“我也听”和“原声直通”。',
    },
    {
      title: '选择两个声音',
      content:
        '点击任一行下方的声音名称，或打开<strong>设置 → 高级 → 提供商</strong>。<strong>对方听到的声音</strong>是对方听你的翻译时用的声音：预设声音或你自己克隆的声音。<strong>我听到的声音</strong>用来把对方的翻译朗读给你听，只能选择预设声音。默认情况下两者是不同的声音。更多内容请参阅<a href="/docs/kizuna-ai/voices">声音指南</a>。',
      screenshot: '04-voices.png',
    },
    {
      title: '开始并轮流说话',
      content:
        '点击<strong>开始</strong>，轮流说话。每句话都会连同翻译一起显示，并读进听的人的耳朵：你的话读给对方，对方的话读给你。“开始”上方的一栏显示每个人的耳朵、语言和设备，此处为 <strong>L 中文 · 我</strong>和 <strong>R 英语 · 对方</strong>；每条翻译都带有 <strong>L</strong> 或 <strong>R</strong> 标记，表示它在哪只耳朵播放。带有划掉的扬声器标记的行没有播放：它的翻译成了说话人自己的语言（有人在一句话里混用两种语言时可能出现），因此不会读给任何人听。',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '轮流说话，并在每句话结束时稍作停顿。两个人同时说话时，一个麦克风听到的是混在一起的声音。',
      '各自说自己的语言。用对方的语言说的话可能会被翻译成你自己的语言，这时它不会读给任何人听。',
      '会话开始后，“对方听到的翻译”和“我听到的翻译”就固定了。要更改，请先停止会话。',
      '对话结束后请停止会话：正在运行的会话会持续消耗你的余额。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '可以只用字幕吗？',
        answer:
          '可以。在设置向导中选择“面对面对话，只要字幕”，或者在开始前于“语音”部分关闭“对方听到的翻译”和“我听到的翻译”。这样你们都看屏幕；在桌面应用中，对方还可以通过字幕共享在手机上跟着看。也可以只关闭其中一个，例如对方更喜欢阅读的时候。',
      },
      {
        question: '面对面对话怎么收费？',
        answer:
          '它始终作为一个共享会话运行：“「两者」模式下共享会话”固定为已启用。带语音时，双方的翻译都会朗读出来，因此每分钟的费用比只有字幕时更高，开始时也需要更高的余额。详情请参阅余额与费用指南。',
      },
      {
        question: '这需要 VB-CABLE 吗？',
        answer: '不需要。面对面对话把两条翻译都播放到你们的耳机里，不会向任何会议应用发送内容。',
      },
      {
        question: '哪些提供商支持面对面对话？',
        answer: '使用 Soniox 引擎的 KizunaAI，以及使用你自己 API 密钥的 Soniox。其他提供商（包括 Qwen 引擎）无法翻译共用一个麦克风的两个人。',
      },
      {
        question: '有交换左右耳的按钮吗？',
        answer: '没有。请在“语音”部分的两行中，或者在从模式切换打开的面板中，分别选择左声道或右声道。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: '別の言語を話す人と対面で会話する',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'このガイドでは、別の言語を話す 2 人が 1 台のパソコンで会話できるように Sokuji デスクトップアプリを設定します。受付での来客対応、テーブルを挟んだ打ち合わせ、親戚との会話などに使えます。2 人で 1 つのマイクを共有し、それぞれ片耳にイヤホンを着けます。あなたの話は相手の言語で相手に、相手の話はあなたの言語であなたに読み上げられ、双方の発言は字幕でも表示されます。会議アプリには何も送られず、仮想オーディオケーブルも不要です。KizunaAI（Soniox 提供。対面での会話には Soniox エンジンが必要です）を使用します。スクリーンショットは Windows 版 Sokuji 0.44.0 のもので、自分は中国語、相手は英語を話し、1 組のイヤホンを分け合って、自分が左耳、相手が右耳に着けています。',
  },
  steps: [
    {
      title: 'KizunaAI で Sokuji をセットアップする',
      content:
        'まだの場合は、<a href="/docs/kizuna-ai/quick-start">KizunaAI クイックスタート</a>に沿って Sokuji をインストールし、サインインしてください。',
    },
    {
      title: '「対面で会話」を選ぶ',
      content:
        'セットアップガイドで<strong>対面で会話</strong>を選びます。Sokuji が<strong>両方</strong>モードになり、相手は隣にいる設定になって、双方の翻訳を読み上げます。すでにセットアップを終えている場合は、代わりに次のステップを使ってください。',
      screenshot: '01-scenario.png',
    },
    {
      title: '相手を隣に設定する',
      content:
        'メイン画面左下のモード切り替えで<strong>両方</strong>をクリックします。パネルが開かない場合は、もう一度「両方」をクリックしてください。<strong>相手の場所</strong>で<strong>隣にいる</strong>を選びます。モード切り替えに<strong>対面</strong>タグが付き、パネルには対面で使うもの、つまりあなたのマイクと、それぞれの翻訳が再生される場所が表示されます。',
      screenshot: '02-other-side.png',
      tip: '「相手の音声」は不要です。相手はあなたのマイクに向かって話し、Sokuji はシステム音声をキャプチャしません。セッションの実行中は「相手の場所」を変更できません。',
    },
    {
      title: '2 つの言語を設定する',
      content:
        '設定の<strong>翻訳言語</strong>で、<strong>話す言語</strong>を自分の言語に、<strong>相手が聞く言語</strong>を相手の言語に設定します。その下の行には逆方向が表示されます。ここでは、話す言語は中国語、相手が聞く言語は英語で、その下の行は「相手は英語を話す → 自分は中国語で聞く」です。',
      tip: '「話す言語」を「自動検出」のままにしないでください。両方モードでは、相手の発言をあなたの言語に翻訳するために Sokuji があなたの言語を知る必要があり、言語を選ぶまで「開始」は押せません。',
    },
    {
      title: '2 人で使うマイクを 1 つ選ぶ',
      content:
        '<strong>マイクロフォン</strong>で、2 人の声を両方拾えるマイクを選びます。たとえば 2 人の間に置いたノートパソコンの内蔵マイクや、卓上マイクです。ヘッドセットのマイクは、着けている人の声しか拾いません。',
    },
    {
      title: 'イヤホンを着けて左右を設定する',
      content:
        'あなたは左耳、相手は右耳にイヤホンを着けます。<strong>音声</strong>セクションでは、<strong>相手に聞こえる翻訳</strong>があなたの翻訳を相手の耳（<strong>右チャンネル</strong>）に、<strong>自分に聞こえる翻訳</strong>が相手の翻訳をあなたの耳（<strong>左チャンネル</strong>）に再生します。どちらも既定の再生デバイスで再生されます。各行の横の再生ボタンを押すと、その耳で短い音が鳴ります。左右を入れ替えるには、各行でもう一方のチャンネルを選びます。それぞれ自分のヘッドセットを使う場合は、各行でデバイスを選びます。左右とデバイスはセッション中でも変更できます。',
      screenshot: '03-speech.png',
      tip: 'イヤホンは片耳ずつ使い、スピーカーは使わないでください。スピーカーから流すと、マイクが翻訳音声を拾ってもう一度翻訳してしまいます。対面では「自分にも再生」と「原音パススルー」は表示されません。',
    },
    {
      title: '2 つの音声を選ぶ',
      content:
        'どちらかの行の下にある音声名をクリックするか、<strong>設定 → 詳細 → プロバイダー</strong>を開きます。<strong>相手に聞こえる音声</strong>は、相手があなたの翻訳を聞くときの音声で、プリセットか、複製した自分の声を選べます。<strong>自分に聞こえる音声</strong>は相手の翻訳をあなたに読み上げる音声で、プリセット音声から選びます。既定では 2 つは別の音声です。詳しくは<a href="/docs/kizuna-ai/voices">音声ガイド</a>を参照してください。',
      screenshot: '04-voices.png',
    },
    {
      title: '開始して交互に話す',
      content:
        '<strong>開始</strong>をクリックし、交互に話します。各文は翻訳とともに表示され、聞く人の耳に読み上げられます。あなたの話は相手の耳に、相手の話はあなたの耳に届きます。「開始」の上の帯には、それぞれの耳、言語、デバイスが表示されます。ここでは <strong>L 中国語 · 自分</strong>と <strong>R 英語 · 相手</strong>です。翻訳された各行には、再生される耳を示す <strong>L</strong> または <strong>R</strong> のタグが付きます。斜線の入ったスピーカーのマークが付いた行は再生されません。その翻訳が話した人自身の言語になってしまったためで、2 つの言語を混ぜて話すと起こることがあります。その行は誰にも読み上げられません。',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      '交互に話し、文の終わりごとに少し間を置いてください。2 人が同時に話すと、1 つのマイクには 2 人の声が混ざって聞こえます。',
      'それぞれ自分の言語で話してください。相手の言語で話した文は、あなたの言語に翻訳されてしまうことがあり、その場合は誰にも読み上げられません。',
      '「相手に聞こえる翻訳」と「自分に聞こえる翻訳」は、セッションが始まると固定されます。変更するには、セッションを停止してください。',
      '会話が終わったらセッションを停止してください。実行中のセッションは残高を消費し続けます。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '字幕だけで使えますか？',
        answer:
          'はい。セットアップガイドで「対面で会話（字幕のみ）」を選ぶか、開始する前に「音声」セクションで「相手に聞こえる翻訳」と「自分に聞こえる翻訳」をオフにします。その場合は 2 人とも画面を読みます。デスクトップアプリでは、相手が字幕共有を使ってスマートフォンで内容を追うこともできます。2 つのうち片方だけをオフにすることもできます。たとえば、相手が読むほうを好む場合です。',
      },
      {
        question: '対面での会話の料金はどのくらいですか？',
        answer:
          '常に 1 つの共有セッションとして実行され、「「両方」モードでセッションを共有」は「有効」に固定されます。音声ありでは、あなたの翻訳と相手の翻訳の両方が読み上げられるため、1 分あたりの料金は字幕のみより高くなり、開始に必要な残高も多くなります。詳しくは料金ガイドを参照してください。',
      },
      {
        question: 'VB-CABLE は必要ですか？',
        answer: 'いいえ。対面では両方の翻訳をイヤホンで再生し、会議アプリには何も送りません。',
      },
      {
        question: 'どのプロバイダーで対面会話ができますか？',
        answer: 'Soniox エンジンの KizunaAI と、自分の API キーを使う Soniox です。Qwen エンジンを含め、ほかのプロバイダーでは、1 つのマイクを使う 2 人の発言を翻訳できません。',
      },
      {
        question: '左右を入れ替えるボタンはありますか？',
        answer: 'いいえ。「音声」セクションの 2 つの行それぞれで、またはモード切り替えから開くパネルで、左チャンネルか右チャンネルを選んでください。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: '두 언어로 마주 보고 대화하기',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      '이 가이드는 서로 다른 언어를 쓰는 두 사람이 한 대의 컴퓨터 앞에서 대화할 수 있도록 Sokuji 데스크톱 앱을 설정합니다. 안내 데스크의 방문객, 테이블을 사이에 둔 회의, 친척과의 대화 등에 쓸 수 있습니다. 두 사람은 마이크 하나를 함께 쓰고 이어버드를 한쪽씩 낍니다. 내가 한 말은 상대방의 언어로 상대방에게 읽어 주고, 상대방이 한 말은 내 언어로 나에게 읽어 주며, 양쪽의 말이 자막으로도 표시됩니다. 회의 앱에는 아무것도 전송되지 않으며 가상 오디오 케이블도 필요 없습니다. KizunaAI(Soniox 제공이며, 마주 보고 대화에는 Soniox 엔진이 필요합니다)를 사용합니다. 스크린샷은 Windows용 Sokuji 0.44.0에서 촬영했습니다. 내가 말하는 언어는 중국어, 상대방이 말하는 언어는 영어이며, 이어버드 한 쌍을 나눠 껴서 나는 왼쪽 귀에, 상대방은 오른쪽 귀에 꼈습니다.',
  },
  steps: [
    {
      title: 'KizunaAI로 Sokuji 설정',
      content:
        '아직 하지 않았다면 <a href="/docs/kizuna-ai/quick-start">KizunaAI 빠른 시작</a>을 따라 Sokuji를 설치하고 로그인하세요.',
    },
    {
      title: '“마주 보고 대화” 선택',
      content:
        '설정 가이드에서 <strong>마주 보고 대화</strong>를 선택하세요. Sokuji가 상대방이 내 옆에 있는 <strong>모두</strong> 모드로 설정되고, 양쪽의 번역을 모두 소리 내어 읽어 줍니다. 이미 설정을 마쳤다면 대신 다음 단계를 따르세요.',
      screenshot: '01-scenario.png',
    },
    {
      title: '상대방 위치를 “내 옆”으로 설정',
      content:
        '메인 창 왼쪽 아래의 모드 전환에서 <strong>모두</strong>를 클릭하세요. 패널이 열리지 않으면 “모두”를 한 번 더 클릭하세요. <strong>상대방 위치</strong>에서 <strong>내 옆</strong>을 선택하세요. 이제 모드 전환에 <strong>대면</strong> 태그가 붙고, 패널에는 마주 보고 대화할 때 쓰는 것, 즉 내 마이크와 각 번역이 재생되는 곳이 표시됩니다.',
      screenshot: '02-other-side.png',
      tip: '“상대방 오디오”는 필요 없습니다. 상대방은 내 마이크에 대고 말하며, Sokuji는 시스템 오디오를 캡처하지 않습니다. 세션이 실행되는 동안에는 “상대방 위치”를 바꿀 수 없습니다.',
    },
    {
      title: '두 언어 설정',
      content:
        '설정의 <strong>번역 언어</strong>에서 <strong>내가 말하는 언어</strong>를 내 언어로, <strong>상대방이 듣는 언어</strong>를 상대방의 언어로 설정하세요. 아래 줄에는 반대 방향이 표시됩니다. 여기서는 내가 말하는 언어는 중국어, 상대방이 듣는 언어는 영어이며, 아래 줄에는 “상대방은 영어로 말함 → 나는 중국어로 들음”이라고 표시됩니다.',
      tip: '“내가 말하는 언어”를 “자동 감지”로 두지 마세요. 모두 모드에서는 상대방의 말을 내 언어로 번역하려면 Sokuji가 내 언어를 알아야 하므로, 언어를 고를 때까지 “시작”이 비활성화됩니다.',
    },
    {
      title: '두 사람이 함께 쓸 마이크 하나 선택',
      content:
        '<strong>마이크</strong>에서 두 사람의 목소리를 모두 받을 수 있는 마이크를 고르세요. 예를 들어 두 사람 사이에 놓은 노트북의 내장 마이크나 탁상용 마이크입니다. 헤드셋의 마이크는 그것을 쓴 사람의 목소리만 받습니다.',
    },
    {
      title: '이어버드를 끼고 좌우 귀 설정',
      content:
        '나는 왼쪽 이어버드를, 상대방은 오른쪽 이어버드를 낍니다. <strong>음성</strong> 섹션에서 <strong>상대방에게 들리는 번역</strong>은 내 번역을 상대방의 귀(<strong>오른쪽 채널</strong>)로, <strong>내가 듣는 번역</strong>은 상대방의 번역을 내 귀(<strong>왼쪽 채널</strong>)로 재생하며, 둘 다 기본 재생 장치를 사용합니다. 각 행 옆의 재생 버튼을 누르면 그 귀에 짧은 신호음이 재생됩니다. 좌우를 바꾸려면 각 행에서 다른 채널을 고르세요. 각자 자기 헤드셋을 쓰려면 각 행에서 장치를 고르세요. 귀와 장치는 세션 중에도 바꿀 수 있습니다.',
      screenshot: '03-speech.png',
      tip: '이어버드는 한쪽씩 쓰고 스피커는 쓰지 마세요. 스피커로 재생하면 마이크가 번역 음성을 다시 받아 한 번 더 번역합니다. 마주 보고 대화할 때는 “나도 듣기”와 “원음 패스스루”가 표시되지 않습니다.',
    },
    {
      title: '두 음성 선택',
      content:
        '두 행 중 어느 쪽이든 아래의 음성 이름을 클릭하거나 <strong>설정 → 고급 → 제공자</strong>를 여세요. <strong>상대방에게 들리는 음성</strong>은 상대방이 내 번역을 듣는 음성으로, 프리셋 또는 내가 복제한 목소리입니다. <strong>내가 듣는 음성</strong>은 상대방의 번역을 나에게 읽어 주며, 프리셋 음성 중에서 고릅니다. 기본적으로 두 음성은 서로 다릅니다. 자세한 내용은 <a href="/docs/kizuna-ai/voices">음성 가이드</a>를 참고하세요.',
      screenshot: '04-voices.png',
    },
    {
      title: '시작하고 번갈아 말하기',
      content:
        '<strong>시작</strong>을 클릭하고 번갈아 말하세요. 각 문장은 번역과 함께 표시되고, 듣는 사람의 귀에 읽어 줍니다. 내 말은 상대방의 귀에, 상대방의 말은 내 귀에 들립니다. “시작” 위쪽 영역에는 각 사람의 귀, 언어, 장치가 표시됩니다. 여기서는 <strong>L 중국어 · 나</strong>와 <strong>R 영어 · 상대방</strong>입니다. 번역된 각 문장에는 재생되는 귀를 나타내는 <strong>L</strong> 또는 <strong>R</strong> 태그가 붙습니다. 스피커에 사선이 그어진 아이콘이 붙은 문장은 재생되지 않은 것입니다. 번역이 말한 사람 자신의 언어로 나온 경우로, 누군가 두 언어를 섞어 말할 때 생길 수 있으며, 이때는 아무에게도 읽어 주지 않습니다.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '번갈아 말하고 각 문장이 끝날 때 잠깐 멈추세요. 두 사람이 동시에 말하면 마이크 하나에 두 목소리가 섞여 들어갑니다.',
      '각자 자기 언어로 말하세요. 상대방의 언어로 말한 문장은 내 언어로 번역되어 나올 수 있으며, 그러면 아무에게도 읽어 주지 않습니다.',
      '“상대방에게 들리는 번역”과 “내가 듣는 번역”은 세션이 실행되면 고정됩니다. 바꾸려면 세션을 중지하세요.',
      '대화가 끝나면 세션을 중지하세요. 실행 중인 세션은 계속 잔액을 사용합니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '자막만 쓸 수 있나요?',
        answer:
          '네. 설정 가이드에서 “마주 보고 대화, 자막만”을 선택하거나, 시작하기 전에 “음성” 섹션에서 “상대방에게 들리는 번역”과 “내가 듣는 번역”을 끄세요. 그러면 두 사람 모두 화면을 읽으며, 데스크톱 앱에서는 상대방이 자막 공유를 통해 휴대폰으로 따라 볼 수도 있습니다. 둘 중 하나만 끌 수도 있습니다. 예를 들어 상대방이 읽는 쪽을 더 좋아할 때입니다.',
      },
      {
        question: '마주 보고 대화하면 비용은 얼마인가요?',
        answer:
          '항상 하나의 공유 세션으로 실행되며, “모두 모드에서 세션 공유”는 “활성화됨”으로 고정됩니다. 음성을 사용하면 내 번역과 상대방의 번역 양쪽이 모두 음성으로 나오므로, 자막만 사용할 때보다 분당 비용이 높고 시작하는 데 더 많은 잔액이 필요합니다. 자세한 내용은 잔액 및 비용 가이드를 참고하세요.',
      },
      {
        question: '이 경우에도 VB-CABLE이 필요한가요?',
        answer: '아니요. 마주 보고 대화할 때는 두 번역을 모두 이어버드로 재생하며, 회의 앱에는 아무것도 보내지 않습니다.',
      },
      {
        question: '어떤 제공자가 마주 보고 대화를 지원하나요?',
        answer: 'Soniox 엔진을 쓰는 KizunaAI, 그리고 내 API 키를 사용하는 Soniox입니다. Qwen 엔진을 포함한 다른 제공자는 마이크 하나를 함께 쓰는 두 사람의 말을 번역할 수 없습니다.',
      },
      {
        question: '좌우 귀를 바꾸는 버튼이 있나요?',
        answer: '아니요. “음성” 섹션의 두 행 각각에서, 또는 모드 전환에서 열리는 패널에서 왼쪽 또는 오른쪽 채널을 고르세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Conversación cara a cara entre dos idiomas',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'Esta guía configura la aplicación de escritorio de Sokuji para dos personas que hablan idiomas distintos ante un mismo ordenador: un visitante en una recepción, una reunión en torno a una mesa, una conversación con un familiar. Las dos personas comparten un micrófono y cada una lleva un auricular. Lo que dices se le lee a la otra persona en su idioma, lo que dice ella se te lee a ti en el tuyo, y ambos lados aparecen como subtítulos. No se envía nada a ninguna aplicación de reuniones y no necesitas un cable de audio virtual. Usa KizunaAI (Con tecnología de Soniox; la conversación cara a cara necesita el motor de Soniox). Las capturas son de Sokuji 0.44.0 en Windows: yo hablo chino, la otra persona habla inglés y compartimos un par de auriculares, yo con el izquierdo y ella con el derecho.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Si aún no lo has hecho, sigue el <a href="/docs/kizuna-ai/quick-start">inicio rápido de KizunaAI</a> para instalar Sokuji e iniciar sesión.',
    },
    {
      title: 'Elige «Conversación cara a cara»',
      content:
        'En la guía de configuración, elige <strong>Conversación cara a cara</strong>. Pone Sokuji en modo <strong>Ambos</strong> con la otra persona a tu lado, y lee en voz alta las dos traducciones. Si ya terminaste la configuración, usa en su lugar el paso siguiente.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Indica que la otra persona está a tu lado',
      content:
        'Haz clic en <strong>Ambos</strong> en el selector de modo, abajo a la izquierda de la ventana principal; si su panel no se abre, vuelve a hacer clic en Ambos. En <strong>¿Dónde está el otro?</strong>, elige <strong>A mi lado</strong>. El selector de modo lleva ahora la etiqueta <strong>Cara a cara</strong>, y el panel muestra lo que usa el modo cara a cara: tu micrófono y dónde se reproduce cada traducción.',
      screenshot: '02-other-side.png',
      tip: 'Audio del otro no hace falta: la otra persona habla a tu micrófono, y Sokuji no captura el audio del sistema. La opción «¿Dónde está el otro?» no se puede cambiar mientras hay una sesión en marcha.',
    },
    {
      title: 'Configura los dos idiomas',
      content:
        'En Configuración, en <strong>Idiomas de Traducción</strong>, pon en <strong>Yo hablo</strong> tu idioma y en <strong>ellos escuchan</strong> el de la otra persona. La línea de debajo muestra la dirección inversa. Aquí: Yo hablo Chino, ellos escuchan Inglés, y la línea dice «Ellos hablan Inglés → yo escucho Chino».',
      tip: 'No dejes Yo hablo en Detección Automática. En el modo Ambos, Sokuji necesita saber tu idioma para traducir a él lo que dice la otra persona, e Iniciar sigue desactivado hasta que elijas uno.',
    },
    {
      title: 'Elige un solo micrófono para las dos personas',
      content:
        'En <strong>Micrófono</strong>, elige un micrófono que oiga a las dos personas, como el micrófono integrado de un portátil colocado entre ambas, o un micrófono de mesa. El micrófono de unos auriculares solo oye a quien los lleva puestos.',
    },
    {
      title: 'Ponte los auriculares y elige los oídos',
      content:
        'Tú llevas el auricular izquierdo y la otra persona el derecho. En la sección <strong>Voz</strong>, <strong>Traducción que oye la otra parte</strong> reproduce tu traducción en el oído de la otra persona (<strong>canal derecho</strong>), y <strong>Traducción que yo oigo</strong> reproduce la suya en el tuyo (<strong>canal izquierdo</strong>), ambas en el dispositivo de reproducción predeterminado. El botón de reproducción junto a cada fila reproduce un tono corto en ese oído. Para intercambiar los oídos, elige el otro canal en cada fila; para que cada persona use sus propios auriculares, elige un dispositivo en cada fila. Los oídos y los dispositivos también se pueden cambiar durante una sesión.',
      screenshot: '03-speech.png',
      tip: 'Un auricular para cada persona, nunca un altavoz: con un altavoz, el micrófono capta la traducción y la vuelve a traducir. Yo también la oigo y Paso directo no aparecen en el modo cara a cara.',
    },
    {
      title: 'Elige las dos voces',
      content:
        'Haz clic en el nombre de la voz bajo cualquiera de las dos filas, o abre <strong>Configuración → Avanzado → Proveedor</strong>. <strong>Voz que oye la otra parte</strong> es la voz con la que la otra persona oye tu traducción: una voz predefinida o tu propia voz clonada. <strong>Voz que yo oigo</strong> te lee la traducción de la otra persona, con una de las voces predefinidas. De forma predeterminada son dos voces distintas. En la <a href="/docs/kizuna-ai/voices">guía de voces</a> encontrarás más información.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Empieza y habla por turnos',
      content:
        'Haz clic en <strong>Iniciar</strong> y habla por turnos con la otra persona. Cada frase aparece con su traducción y se lee en el oído de quien escucha: la tuya en el de la otra persona, la suya en el tuyo. La franja situada sobre Iniciar muestra el oído, el idioma y el dispositivo de cada persona, aquí <strong>L Chino · yo</strong> y <strong>R Inglés · el otro</strong>, y cada línea traducida lleva una etiqueta <strong>L</strong> o <strong>R</strong> según el oído en el que se reproduce. Una línea marcada con un altavoz tachado no se reproduce: su traducción salió en el propio idioma de quien habla, lo que puede ocurrir cuando alguien mezcla los dos idiomas, así que no se lee a nadie.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Habla por turnos y haz una breve pausa al final de cada frase. Si las dos personas hablan a la vez, un solo micrófono las oye mezcladas.',
      'Cíñete a tu propio idioma. Una frase en el idioma de la otra persona puede acabar traducida a tu propio idioma, y entonces no se lee a nadie.',
      'Traducción que oye la otra parte y Traducción que yo oigo quedan fijadas en cuanto hay una sesión en marcha. Detén la sesión para cambiarlas.',
      'Detén la sesión cuando termine la conversación: una sesión en marcha sigue consumiendo tu saldo.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Podemos usar solo subtítulos?',
        answer:
          'Sí. Elige «Conversación cara a cara, solo subtítulos» en la guía de configuración, o desactiva Traducción que oye la otra parte y Traducción que yo oigo en la sección Voz antes de empezar. Entonces las dos personas leen la pantalla, y en la aplicación de escritorio la otra persona también puede seguir la conversación en su teléfono con la función Compartir subtítulos. También puedes desactivar solo una de las dos, por ejemplo cuando la otra persona prefiere leer.',
      },
      {
        question: '¿Cuánto cuesta una conversación cara a cara?',
        answer:
          'Siempre funciona como una sola sesión compartida: Sesión compartida en el modo Ambos está fijada en Habilitado. Con voz se pronuncian las dos mitades, tu traducción y la de la otra persona, así que cuesta más por minuto que solo subtítulos y necesita más saldo para empezar. La guía de saldo y costes tiene los detalles.',
      },
      {
        question: '¿Necesito VB-CABLE para esto?',
        answer: 'No. El modo cara a cara reproduce las dos traducciones en los auriculares y no envía nada a ninguna aplicación de reuniones.',
      },
      {
        question: '¿Qué proveedores admiten el modo cara a cara?',
        answer: 'KizunaAI con su motor de Soniox, y Soniox con tu propia clave API. Los demás proveedores, incluido el motor de Qwen, no pueden traducir a dos personas con un solo micrófono.',
      },
      {
        question: '¿Hay un botón para intercambiar los oídos?',
        answer: 'No. Elige el canal izquierdo o derecho en cada una de las dos filas de la sección Voz, o en el panel que se abre desde el selector de modo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Conversation en face à face entre deux langues',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'Ce guide configure l’application de bureau Sokuji pour deux personnes devant un même ordinateur qui parlent des langues différentes : un visiteur à l’accueil, une réunion autour d’une table, une conversation avec un proche. Vous partagez un seul microphone et portez chacun un écouteur. Ce que vous dites est lu à l’autre personne dans sa langue, ce qu’elle dit vous est lu dans la vôtre, et les deux côtés s’affichent en sous-titres. Rien n’est envoyé dans une application de réunion, et vous n’avez besoin d’aucun câble audio virtuel. Il utilise KizunaAI (Propulsé par Soniox ; la conversation en face à face nécessite le moteur Soniox). Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows : je parle chinois, l’autre personne parle anglais, et nous partageons une paire d’écouteurs, le mien dans l’oreille gauche et le sien dans la droite.',
  },
  steps: [
    {
      title: 'Configurez Sokuji avec KizunaAI',
      content:
        'Si ce n’est pas encore fait, suivez le <a href="/docs/kizuna-ai/quick-start">démarrage rapide KizunaAI</a> pour installer Sokuji et vous connecter.',
    },
    {
      title: 'Choisissez « Conversation en face à face »',
      content:
        'Dans le guide de configuration, choisissez <strong>Conversation en face à face</strong>. Sokuji passe alors en mode <strong>Les deux</strong>, avec l’autre partie à côté de vous, et lit les deux traductions à voix haute. Si vous avez déjà terminé la configuration, passez plutôt à l’étape suivante.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Indiquez que l’autre personne est à côté de vous',
      content:
        'Cliquez sur <strong>Les deux</strong> dans le sélecteur de mode, en bas à gauche de la fenêtre principale ; si son panneau ne s’ouvre pas, cliquez de nouveau sur Les deux. Sous <strong>Où est l\'autre ?</strong>, choisissez <strong>À côté de moi</strong>. Le sélecteur de mode porte maintenant l’étiquette <strong>Face à face</strong>, et le panneau montre ce qu’utilise le face à face : votre microphone, et l’endroit où chaque traduction est lue.',
      screenshot: '02-other-side.png',
      tip: 'Inutile de régler Audio de l\'autre : l’autre personne parle dans votre microphone, et Sokuji ne capture aucun audio système. Le réglage « Où est l\'autre ? » ne peut pas être modifié pendant une session.',
    },
    {
      title: 'Réglez les deux langues',
      content:
        'Dans les Paramètres, sous <strong>Langues de Traduction</strong>, réglez <strong>je parle</strong> sur votre langue et <strong>ils entendent</strong> sur celle de l’autre personne. La ligne en dessous indique le sens inverse. Ici : je parle chinois, ils entendent anglais, et la ligne indique « Ils parlent Anglais → j\'entends Chinois ».',
      tip: 'Ne laissez pas je parle sur Détection Automatique. En mode Les deux, Sokuji doit connaître votre langue pour y traduire les paroles de l’autre personne, et Démarrer reste désactivé tant que vous n’en avez pas choisi une.',
    },
    {
      title: 'Choisissez un seul microphone pour vous deux',
      content:
        'Sous <strong>Microphone</strong>, sélectionnez un microphone qui vous entend tous les deux, par exemple le microphone intégré d’un ordinateur portable placé entre vous, ou un microphone de bureau. Le microphone d’un casque n’entend que la personne qui le porte.',
    },
    {
      title: 'Mettez les écouteurs et réglez les oreilles',
      content:
        'Vous portez l’écouteur gauche, l’autre personne le droit. Dans la section <strong>Voix</strong>, <strong>Traduction entendue par l\'autre</strong> lit votre traduction dans l’oreille de l’autre personne (<strong>canal droit</strong>), et <strong>Traduction que j\'entends</strong> lit la sienne dans la vôtre (<strong>canal gauche</strong>), toutes deux sur l’appareil de lecture par défaut. Le bouton de lecture à côté de chaque ligne joue un court signal sonore dans cette oreille. Pour inverser les oreilles, choisissez l’autre canal dans chaque ligne ; pour que chacun ait son propre casque, choisissez un appareil dans chaque ligne. Les oreilles et les appareils peuvent aussi changer pendant une session.',
      screenshot: '03-speech.png',
      tip: 'Un écouteur chacun, jamais de haut-parleur : avec un haut-parleur, le microphone capte la traduction et la traduit de nouveau. « Je l\'entends aussi » et « Passage direct » ne sont pas affichés en face à face.',
    },
    {
      title: 'Choisissez les deux voix',
      content:
        'Cliquez sur le nom de la voix sous l’une ou l’autre ligne, ou ouvrez <strong>Paramètres → Avancé → Fournisseur</strong>. <strong>Voix entendue par l\'autre</strong> est la voix dans laquelle l’autre personne entend votre traduction : une voix prédéfinie ou votre propre voix clonée. <strong>Voix que j\'entends</strong> vous lit la traduction de l’autre personne, parmi les voix prédéfinies. Par défaut, ce sont deux voix différentes. Le <a href="/docs/kizuna-ai/voices">guide des voix</a> en dit plus.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Démarrez et parlez à tour de rôle',
      content:
        'Cliquez sur <strong>Démarrer</strong> et parlez chacun votre tour. Chaque phrase s’affiche avec sa traduction et est lue dans l’oreille de la personne qui écoute : vos phrases dans celle de l’autre personne, les siennes dans la vôtre. Le bandeau au-dessus de Démarrer indique l’oreille, la langue et l’appareil de chacun, ici <strong>L Chinois · moi</strong> et <strong>R Anglais · l\'autre</strong>, et chaque ligne traduite porte une étiquette <strong>L</strong> ou <strong>R</strong> pour l’oreille où elle est lue. Une ligne marquée d’un haut-parleur barré n’est pas lue : sa traduction est sortie dans la langue de la personne qui a parlé, ce qui peut arriver quand quelqu’un mélange les deux langues, et elle n’est donc lue à personne.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Parlez à tour de rôle et marquez une pause à la fin de chaque phrase. Quand vous parlez tous les deux à la fois, un seul microphone entend vos deux voix mélangées.',
      'Tenez-vous-en à votre propre langue. Une phrase dans la langue de l’autre personne peut ressortir traduite dans la vôtre, et elle n’est alors lue à personne.',
      '« Traduction entendue par l\'autre » et « Traduction que j\'entends » sont figées dès qu’une session est en cours. Arrêtez la session pour les modifier.',
      'Arrêtez la session à la fin de la conversation : une session en cours continue de consommer votre solde.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Pouvons-nous utiliser uniquement les sous-titres ?',
        answer:
          'Oui. Choisissez « Conversation en face à face, sous-titres uniquement » dans le guide de configuration, ou désactivez « Traduction entendue par l\'autre » et « Traduction que j\'entends » dans la section Voix avant de démarrer. Vous lisez alors tous les deux l’écran, et dans l’application de bureau l’autre personne peut aussi suivre sur son téléphone grâce au partage des sous-titres. Vous pouvez aussi n’en désactiver qu’une des deux, par exemple quand l’autre personne préfère lire.',
      },
      {
        question: 'Combien coûte une conversation en face à face ?',
        answer:
          'Elle fonctionne toujours en une seule session partagée : le réglage « Session partagée en mode Les deux » est fixé sur Activé. Avec voix, les deux moitiés sont prononcées, votre traduction et celle de l’autre personne : elle coûte donc plus cher par minute que les sous-titres seuls et nécessite un solde plus élevé pour démarrer. Le guide sur le solde et les coûts donne les détails.',
      },
      {
        question: 'Ai-je besoin de VB-CABLE pour cela ?',
        answer: 'Non. Le face à face lit les deux traductions dans vos écouteurs et n’envoie rien dans une application de réunion.',
      },
      {
        question: 'Quels fournisseurs permettent le face à face ?',
        answer: 'KizunaAI avec son moteur Soniox, et Soniox avec votre propre clé API. Les autres fournisseurs, le moteur Qwen compris, ne peuvent pas traduire deux personnes qui partagent un même microphone.',
      },
      {
        question: 'Y a-t-il un bouton pour inverser les oreilles ?',
        answer: 'Non. Choisissez le canal gauche ou droit dans chacune des deux lignes de la section Voix, ou dans le panneau qui s’ouvre depuis le sélecteur de mode.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'محادثة وجهًا لوجه بلغتين مختلفتين',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يُعدّ هذا الدليل تطبيق Sokuji لسطح المكتب لشخصين أمام حاسوب واحد يتحدثان لغتين مختلفتين: زائر عند مكتب الاستقبال، أو اجتماع حول طاولة، أو حديث مع أحد الأقارب. تتشاركان ميكروفونًا واحدًا، ويضع كلٌّ منكما سماعة أذن واحدة. ما تقوله يُقرأ على الشخص الآخر بلغته، وما يقوله يُقرأ عليك بلغتك، ويظهر كلام الطرفين كترجمة نصية. لا يُرسَل أي شيء إلى تطبيق اجتماعات، ولا تحتاج إلى كابل صوت افتراضي. يستخدم الدليل KizunaAI (مدعوم من Soniox؛ والمحادثة وجهًا لوجه تحتاج إلى محرك Soniox). لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows: أتحدث الصينية، ويتحدث الشخص الآخر الإنجليزية، ونتشارك زوجًا من سماعات الأذن، سماعتي في الأذن اليسرى وسماعته في اليمنى.',
  },
  steps: [
    {
      title: 'اضبط Sokuji مع KizunaAI',
      content:
        'إذا لم تفعل ذلك بعد، فاتبع <a href="/docs/kizuna-ai/quick-start">البدء السريع مع KizunaAI</a> لتثبيت Sokuji وتسجيل الدخول.',
    },
    {
      title: 'اختر «محادثة وجهًا لوجه»',
      content:
        'في دليل الإعداد، اختر <strong>محادثة وجهًا لوجه</strong>. يضع ذلك Sokuji في وضع <strong>الكل</strong> مع الطرف الآخر بجانبك، ويقرأ الترجمتين كلتيهما بصوت مسموع. إذا كنت قد أنهيت الإعداد بالفعل، فاتبع الخطوة التالية بدلًا من ذلك.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'أخبر Sokuji أن الطرف الآخر بجانبك',
      content:
        'انقر على <strong>الكل</strong> في مبدّل الأوضاع أسفل يسار النافذة الرئيسية؛ وإذا لم تُفتح لوحته، فانقر على «الكل» مرة أخرى. تحت <strong>مكان الطرف الآخر</strong>، اختر <strong>بجانبي</strong>. يحمل مبدّل الأوضاع الآن علامة <strong>وجهًا لوجه</strong>، وتعرض اللوحة ما يستخدمه وضع وجهًا لوجه: ميكروفونك، والمكان الذي تُشغَّل فيه كل ترجمة.',
      screenshot: '02-other-side.png',
      tip: 'لا حاجة إلى «صوت الآخر»: فالشخص الآخر يتحدث في ميكروفونك، ولا يلتقط Sokuji أي صوت من النظام. ولا يمكن تغيير «مكان الطرف الآخر» أثناء تشغيل جلسة.',
    },
    {
      title: 'اضبط اللغتين',
      content:
        'في الإعدادات، تحت <strong>لغات الترجمة</strong>، اضبط <strong>أتحدث</strong> على لغتك و<strong>يسمعون</strong> على لغة الشخص الآخر. يعرض السطر أسفلهما الاتجاه المعاكس. هنا: أتحدث الصينية، يسمعون الإنجليزية، ويقول السطر «يتحدثون الإنجليزية ← أسمع الصينية».',
      tip: 'لا تترك «أتحدث» على «اكتشاف تلقائي». في وضع الكل يجب أن يعرف Sokuji لغتك ليترجم كلام الشخص الآخر إليها، ويبقى زر «ابدأ» معطّلًا حتى تختار لغة.',
    },
    {
      title: 'اختر ميكروفونًا واحدًا لكليكما',
      content:
        'تحت <strong>ميكروفون</strong>، اختر ميكروفونًا يسمعكما معًا، مثل الميكروفون المدمج في حاسوب محمول موضوع بينكما، أو ميكروفون مكتبي. أما ميكروفون سماعة الرأس فلا يسمع إلا من يرتديها.',
    },
    {
      title: 'ضع سماعات الأذن واضبط الأذنين',
      content:
        'تضع أنت سماعة الأذن اليسرى، ويضع الشخص الآخر اليمنى. في قسم <strong>الكلام</strong>، تُشغّل <strong>الترجمة التي يسمعها الطرف الآخر</strong> ترجمتك في أذن الشخص الآخر (<strong>القناة اليمنى</strong>)، وتُشغّل <strong>الترجمة التي أسمعها</strong> ترجمته في أذنك (<strong>القناة اليسرى</strong>)، وكلتاهما على جهاز التشغيل الافتراضي. يُشغّل زر التشغيل بجانب كل صف نغمة قصيرة في تلك الأذن. لتبديل الأذنين، اختر القناة الأخرى في كل صف؛ ولكي يستخدم كلٌّ منكما سماعة رأس خاصة به، اختر جهازًا في كل صف. ويمكن تغيير الأذنين والأجهزة أثناء الجلسة أيضًا.',
      screenshot: '03-speech.png',
      tip: 'سماعة أذن واحدة لكل شخص، ولا تستخدم مكبّر صوت أبدًا: مع مكبّر الصوت يلتقط الميكروفون الترجمة ويترجمها مرة أخرى. لا يظهر «أسمعها أنا أيضًا» و«التمرير المباشر» في وضع وجهًا لوجه.',
    },
    {
      title: 'اختر الصوتين',
      content:
        'انقر على اسم الصوت تحت أيٍّ من الصفين، أو افتح <strong>الإعدادات ← متقدم ← المزود</strong>. <strong>الصوت الذي يسمعه الطرف الآخر</strong> هو الصوت الذي يسمع به الشخص الآخر ترجمتك: صوت جاهز أو صوتك المستنسخ. أما <strong>الصوت الذي أسمعه</strong> فيقرأ عليك ترجمة الشخص الآخر، من بين الأصوات الجاهزة. افتراضيًا يكون الصوتان مختلفين. تجد المزيد في <a href="/docs/kizuna-ai/voices">دليل الأصوات</a>.',
      screenshot: '04-voices.png',
    },
    {
      title: 'ابدأ وتحدّثا بالتناوب',
      content:
        'انقر على <strong>ابدأ</strong> وتحدّثا بالتناوب. تظهر كل جملة مع ترجمتها وتُقرأ في أذن المستمع: جملتك في أذن الشخص الآخر، وجملته في أذنك. يعرض الشريط أعلى زر «ابدأ» أذن كل شخص ولغته وجهازه، وهنا <strong>L الصينية · أنا</strong> و<strong>R الإنجليزية · الآخر</strong>، ويحمل كل سطر مترجَم علامة <strong>L</strong> أو <strong>R</strong> تشير إلى الأذن التي يُشغَّل فيها. أما السطر الذي تظهر عليه علامة مكبّر صوت مشطوب فلا يُشغَّل: فقد خرجت ترجمته بلغة المتحدث نفسه، وهو ما قد يحدث عندما يخلط أحدكما بين اللغتين، لذا لا يُقرأ لأحد.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'تحدّثا بالتناوب وتوقّفا لحظة في نهاية كل جملة. عندما تتحدثان في الوقت نفسه، يسمعكما الميكروفون الواحد مختلطَين.',
      'التزم بلغتك. فالجملة التي تقولها بلغة الشخص الآخر قد تخرج مترجمةً إلى لغتك أنت، وعندها لا تُقرأ لأحد.',
      'يُثبَّت خيارا «الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» بمجرد تشغيل الجلسة. أوقف الجلسة لتغييرهما.',
      'أوقف الجلسة عند انتهاء المحادثة: فالجلسة الجارية تستمر في استهلاك رصيدك.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل يمكننا استخدام الترجمة النصية فقط؟',
        answer:
          'نعم. اختر «محادثة وجهًا لوجه، ترجمة نصية فقط» في دليل الإعداد، أو أوقف «الترجمة التي يسمعها الطرف الآخر» و«الترجمة التي أسمعها» في قسم «الكلام» قبل أن تبدأ. عندها تقرآن الشاشة كلاكما، وفي تطبيق سطح المكتب يمكن للشخص الآخر أيضًا المتابعة على هاتفه عبر مشاركة الترجمة النصية. ويمكنك أيضًا إيقاف أحد الخيارين فقط، مثلًا عندما يفضّل الشخص الآخر القراءة.',
      },
      {
        question: 'كم تكلّف المحادثة وجهًا لوجه؟',
        answer:
          'تعمل دائمًا كجلسة مشتركة واحدة: يكون خيار «جلسة مشتركة في وضع الكل» مثبّتًا على «مفعل». ومع الصوت، يُنطق النصفان كلاهما، ترجمتك وترجمة الشخص الآخر، لذا تكلّف أكثر في الدقيقة مقارنةً بالترجمة النصية فقط وتحتاج إلى رصيد أعلى للبدء. يشرح دليل الرصيد والتكاليف التفاصيل.',
      },
      {
        question: 'هل أحتاج إلى VB-CABLE لهذا؟',
        answer: 'لا. يُشغّل وضع وجهًا لوجه الترجمتين في سماعات الأذن لديكما ولا يُرسل أي شيء إلى تطبيق اجتماعات.',
      },
      {
        question: 'أي المزودين يدعمون وضع وجهًا لوجه؟',
        answer: 'KizunaAI مع محرك Soniox الخاص به، وSoniox بمفتاح API الخاص بك. لا يستطيع المزودون الآخرون، بمن فيهم محرك Qwen، ترجمة شخصين على ميكروفون واحد.',
      },
      {
        question: 'هل يوجد زر لتبديل الأذنين؟',
        answer: 'لا. اختر القناة اليسرى أو اليمنى في كلٍّ من صفَّي قسم «الكلام»، أو في اللوحة التي تُفتح من مبدّل الأوضاع.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Conversa cara a cara entre dois idiomas',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'Este guia configura o aplicativo de desktop do Sokuji para duas pessoas em um mesmo computador que falam idiomas diferentes: um visitante na recepção, uma reunião frente a frente, uma conversa com um parente. Vocês compartilham um microfone, e cada um usa um fone. O que você diz é lido para a outra pessoa no idioma dela, o que ela diz é lido para você no seu, e os dois lados aparecem como legendas. Nada é enviado para um aplicativo de reunião, e você não precisa de cabo de áudio virtual. Ele usa a KizunaAI (Com tecnologia Soniox; a conversa cara a cara exige o mecanismo Soniox). As capturas de tela são do Sokuji 0.44.0 no Windows: eu falo chinês, a outra pessoa fala inglês, e compartilhamos um par de fones, o meu no ouvido esquerdo e o dela no direito.',
  },
  steps: [
    {
      title: 'Configure o Sokuji com a KizunaAI',
      content:
        'Se ainda não fez isso, siga o <a href="/docs/kizuna-ai/quick-start">início rápido com KizunaAI</a> para instalar o Sokuji e entrar na sua conta.',
    },
    {
      title: 'Escolha “Conversa cara a cara”',
      content:
        'No guia de configuração, escolha <strong>Conversa cara a cara</strong>. Isso coloca o Sokuji no modo <strong>Ambos</strong>, com a outra pessoa ao seu lado, e lê as duas traduções em voz alta. Se você já concluiu a configuração, siga a próxima etapa em vez disso.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Coloque a outra pessoa ao seu lado',
      content:
        'Clique em <strong>Ambos</strong> no seletor de modo, no canto inferior esquerdo da janela principal; se o painel dele não abrir, clique em Ambos de novo. Em <strong>Onde está o outro</strong>, escolha <strong>Ao meu lado</strong>. O seletor de modo passa a exibir a etiqueta <strong>Cara a cara</strong>, e o painel mostra o que o modo cara a cara usa: o seu microfone e onde cada tradução é reproduzida.',
      screenshot: '02-other-side.png',
      tip: 'Não é preciso definir Áudio do outro: a outra pessoa fala no seu microfone, e o Sokuji não captura o áudio do sistema. “Onde está o outro” não pode ser alterado enquanto uma sessão está em andamento.',
    },
    {
      title: 'Defina os dois idiomas',
      content:
        'Em Configurações, em <strong>Idiomas de Tradução</strong>, defina <strong>eu falo</strong> como o seu idioma e <strong>eles ouvem</strong> como o da outra pessoa. A linha abaixo mostra o sentido inverso. Aqui: eu falo Chinês, eles ouvem Inglês, e a linha diz “Eles falam Inglês → eu ouço Chinês”.',
      tip: 'Não deixe eu falo em Detecção Automática. No modo Ambos, o Sokuji precisa saber o seu idioma para traduzir a outra pessoa para ele, e Iniciar continua desativado até você escolher um.',
    },
    {
      title: 'Escolha um microfone para vocês dois',
      content:
        'Em <strong>Microfone</strong>, escolha um microfone que ouça vocês dois, como o microfone embutido de um notebook colocado entre vocês ou um microfone de mesa. O microfone de um headset só ouve quem o está usando.',
    },
    {
      title: 'Coloque os fones e defina os ouvidos',
      content:
        'Você usa o fone esquerdo, e a outra pessoa, o direito. Na seção <strong>Voz</strong>, <strong>Tradução que o outro lado ouve</strong> reproduz a sua tradução no ouvido da outra pessoa (<strong>canal direito</strong>), e <strong>Tradução que eu ouço</strong> reproduz a dela no seu (<strong>canal esquerdo</strong>), ambas no dispositivo de reprodução padrão. O botão de reprodução ao lado de cada linha toca um tom curto naquele ouvido. Para trocar os ouvidos, escolha o outro canal em cada linha; para que cada um use o próprio headset, escolha um dispositivo em cada linha. Os ouvidos e os dispositivos também podem mudar durante uma sessão.',
      screenshot: '03-speech.png',
      tip: 'Um fone para cada um, nunca um alto-falante: por um alto-falante, o microfone capta a tradução e a traduz de novo. “Eu também ouço” e “Passagem direta” não aparecem no modo cara a cara.',
    },
    {
      title: 'Escolha as duas vozes',
      content:
        'Clique no nome da voz abaixo de qualquer uma das linhas ou abra <strong>Configurações → Avançado → Provedor</strong>. <strong>Voz que o outro lado ouve</strong> é a voz com que a outra pessoa ouve a sua tradução: uma predefinida ou a sua própria voz clonada. <strong>Voz que eu ouço</strong> lê para você a tradução da outra pessoa, entre as vozes predefinidas. Por padrão, as duas são vozes diferentes. O <a href="/docs/kizuna-ai/voices">guia de vozes</a> traz mais detalhes.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Inicie e falem um de cada vez',
      content:
        'Clique em <strong>Iniciar</strong> e falem um de cada vez. Cada frase aparece com a sua tradução e é lida no ouvido de quem escuta: a sua no da outra pessoa, a dela no seu. A faixa acima de Iniciar mostra o ouvido, o idioma e o dispositivo de cada pessoa, aqui <strong>L Chinês · eu</strong> e <strong>R Inglês · o outro</strong>, e cada linha traduzida traz uma etiqueta <strong>L</strong> ou <strong>R</strong> com o ouvido em que é reproduzida. Uma linha marcada com um alto-falante riscado não é reproduzida: a tradução saiu no próprio idioma de quem falou, o que pode acontecer quando alguém mistura os dois idiomas, então ela não é lida para ninguém.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Falem um de cada vez e façam uma pausa no fim de cada frase. Quando vocês dois falam ao mesmo tempo, o único microfone ouve as vozes de vocês misturadas.',
      'Fale no seu próprio idioma. Uma frase no idioma da outra pessoa pode sair traduzida para o seu, e aí não é lida para ninguém.',
      'Tradução que o outro lado ouve e Tradução que eu ouço ficam fixas depois que uma sessão começa. Pare a sessão para alterá-las.',
      'Pare a sessão quando a conversa terminar: uma sessão em andamento continua consumindo seu saldo.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Podemos usar só legendas?',
        answer:
          'Sim. Escolha “Conversa cara a cara, somente legendas” no guia de configuração, ou desative Tradução que o outro lado ouve e Tradução que eu ouço na seção Voz antes de iniciar. Assim, vocês dois leem a tela e, no aplicativo de desktop, a outra pessoa também pode acompanhar pelo celular com o compartilhamento de legendas. Você também pode desativar só uma das duas, por exemplo quando a outra pessoa prefere ler.',
      },
      {
        question: 'Quanto custa uma conversa cara a cara?',
        answer:
          'Ela sempre funciona como uma única sessão compartilhada: Sessão compartilhada no modo Ambos fica fixa em Ativado. Com voz, as duas metades são faladas, a sua tradução e a da outra pessoa, então custa mais por minuto do que só legendas e exige um saldo maior para iniciar. O guia de saldo e custos traz os detalhes.',
      },
      {
        question: 'Preciso do VB-CABLE para isso?',
        answer: 'Não. O modo cara a cara reproduz as duas traduções nos fones de vocês e não envia nada para um aplicativo de reunião.',
      },
      {
        question: 'Quais provedores oferecem o modo cara a cara?',
        answer: 'A KizunaAI com o mecanismo Soniox, e a Soniox com a sua própria chave de API. Outros provedores, incluindo o mecanismo Qwen, não conseguem traduzir duas pessoas em um mesmo microfone.',
      },
      {
        question: 'Existe um botão para trocar os ouvidos?',
        answer: 'Não. Escolha o canal esquerdo ou direito em cada uma das duas linhas da seção Voz, ou no painel que abre a partir do seletor de modo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Разговор лицом к лицу на двух языках',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'Это руководство настраивает настольное приложение Sokuji для двух человек, которые сидят за одним компьютером и говорят на разных языках: посетитель на стойке регистрации, встреча за одним столом, разговор с родственником. Вы пользуетесь одним микрофоном, и у каждого по одному наушнику. Ваши слова зачитываются собеседнику на его языке, его слова — вам на вашем, а речь обеих сторон отображается в виде субтитров. В приложение для встреч ничего не отправляется, и виртуальный аудиокабель не нужен. Используется KizunaAI (на базе Soniox; для разговора лицом к лицу нужен движок Soniox). Снимки экрана сделаны в Sokuji 0.44.0 на Windows: я говорю на китайском, собеседник — на английском, и мы делим одну пару наушников: мой наушник в левом ухе, его — в правом.',
  },
  steps: [
    {
      title: 'Настройте Sokuji с KizunaAI',
      content:
        'Если вы ещё этого не сделали, следуйте руководству <a href="/docs/kizuna-ai/quick-start">«Быстрый старт с KizunaAI»</a>, чтобы установить Sokuji и войти в учётную запись.',
    },
    {
      title: 'Выберите «Разговор лицом к лицу»',
      content:
        'В мастере настройки выберите <strong>Разговор лицом к лицу</strong>. Sokuji переходит в режим <strong>Оба</strong> с собеседником рядом с вами и зачитывает вслух оба перевода. Если вы уже завершили настройку, вместо этого выполните следующий шаг.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Укажите, что собеседник рядом с вами',
      content:
        'Нажмите <strong>Оба</strong> в переключателе режимов в левом нижнем углу главного окна; если его панель не открылась, нажмите «Оба» ещё раз. В разделе <strong>Где собеседник</strong> выберите <strong>Рядом со мной</strong>. Теперь на переключателе режимов есть метка <strong>Лицом к лицу</strong>, а панель показывает, что используется при разговоре лицом к лицу: ваш микрофон и то, где воспроизводится каждый перевод.',
      screenshot: '02-other-side.png',
      tip: '«Аудио собеседника» не нужно: собеседник говорит в ваш микрофон, и Sokuji не захватывает системный звук. Параметр «Где собеседник» нельзя изменить во время сеанса.',
    },
    {
      title: 'Задайте два языка',
      content:
        'В настройках, в разделе <strong>Языки перевода</strong>, укажите в поле <strong>я говорю на</strong> свой язык, а в поле <strong>собеседник слышит</strong> — язык собеседника. Строка под полями показывает обратное направление. Здесь: я говорю на китайском, собеседник слышит английский, а строка под полями гласит «Собеседник говорит на Английский → я слышу Китайский».',
      tip: 'Не оставляйте в поле «я говорю на» значение «Автоопределение». В режиме «Оба» Sokuji должен знать ваш язык, чтобы переводить на него собеседника, и кнопка «Старт» остаётся недоступной, пока вы его не выберете.',
    },
    {
      title: 'Выберите один микрофон для вас обоих',
      content:
        'В разделе <strong>Микрофон</strong> выберите микрофон, который слышит вас обоих, например встроенный микрофон ноутбука, стоящего между вами, или настольный микрофон. Микрофон гарнитуры слышит только того, кто её носит.',
    },
    {
      title: 'Наденьте наушники и настройте левое и правое ухо',
      content:
        'Вы носите левый наушник, собеседник — правый. В блоке <strong>Речь</strong> параметр <strong>Перевод, который слышит собеседник</strong> воспроизводит ваш перевод в ухе собеседника (<strong>правый канал</strong>), а <strong>Перевод, который слышу я</strong> — его перевод в вашем (<strong>левый канал</strong>), оба на устройстве воспроизведения по умолчанию. Кнопка воспроизведения рядом с каждой строкой проигрывает в этом ухе короткий сигнал. Чтобы поменять уши местами, выберите другой канал в каждой строке; чтобы у каждого была своя гарнитура, выберите устройство в каждой строке. Уши и устройства можно менять и во время сеанса.',
      screenshot: '03-speech.png',
      tip: 'По одному наушнику на каждого и никаких колонок: через колонки микрофон улавливает перевод и переводит его снова. При разговоре лицом к лицу «Я тоже слышу» и «Passthrough» не показываются.',
    },
    {
      title: 'Выберите два голоса',
      content:
        'Нажмите на название голоса под любой из строк или откройте <strong>Настройки → Расширенный → Поставщик</strong>. <strong>Голос, который слышит собеседник</strong> — голос, которым собеседник слышит ваш перевод: готовый голос или ваш собственный клонированный голос. <strong>Голос, который слышу я</strong> зачитывает вам перевод собеседника, его выбирают из готовых голосов. По умолчанию это два разных голоса. Подробнее — в <a href="/docs/kizuna-ai/voices">руководстве по голосам</a>.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Начните и говорите по очереди',
      content:
        'Нажмите <strong>Старт</strong> и говорите по очереди. Каждое предложение появляется вместе с переводом и зачитывается в ухо слушающему: ваше — в ухо собеседника, его — в ваше. Полоса над кнопкой «Старт» показывает ухо, язык и устройство каждого, здесь <strong>L Китайский · я</strong> и <strong>R Английский · собеседник</strong>, а у каждой переведённой строки есть метка <strong>L</strong> или <strong>R</strong> — ухо, в котором она воспроизводится. Строка, отмеченная перечёркнутым динамиком, не воспроизводится: её перевод получился на собственном языке говорящего (так бывает, когда кто-то смешивает оба языка), поэтому её никто не услышит.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Говорите по очереди и делайте паузу в конце каждого предложения. Когда вы говорите одновременно, один микрофон слышит ваши голоса вперемешку.',
      'Говорите на своём языке. Предложение на языке собеседника может быть переведено на ваш собственный язык, и тогда его никто не услышит.',
      '«Перевод, который слышит собеседник» и «Перевод, который слышу я» фиксируются, как только сеанс запущен. Чтобы изменить их, остановите сеанс.',
      'Останавливайте сеанс, когда разговор закончен: запущенный сеанс продолжает расходовать ваш баланс.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Можно ли обойтись только субтитрами?',
        answer:
          'Да. Выберите в мастере настройки «Разговор лицом к лицу, только субтитры» или до начала выключите «Перевод, который слышит собеседник» и «Перевод, который слышу я» в блоке «Речь». Тогда вы оба читаете экран, а в настольном приложении собеседник может также следить за разговором на своём телефоне через общий доступ к субтитрам. Можно выключить и только один из двух переключателей, например если собеседник предпочитает читать.',
      },
      {
        question: 'Сколько стоит разговор лицом к лицу?',
        answer:
          'Он всегда идёт как один общий сеанс: «Общий сеанс в режиме «Оба»» зафиксирован на «Включено». С голосом озвучиваются обе половины — ваш перевод и перевод собеседника, поэтому минута стоит дороже, чем только с субтитрами, и для начала сеанса нужен больший баланс. Подробности — в руководстве по оплате.',
      },
      {
        question: 'Нужен ли для этого VB-CABLE?',
        answer: 'Нет. Разговор лицом к лицу воспроизводит оба перевода в ваших наушниках и ничего не отправляет в приложение для встреч.',
      },
      {
        question: 'Какие провайдеры поддерживают разговор лицом к лицу?',
        answer: 'KizunaAI с движком Soniox, а также Soniox с вашим собственным API-ключом. Другие провайдеры, включая движок Qwen, не умеют переводить двух человек у одного микрофона.',
      },
      {
        question: 'Есть ли кнопка, чтобы поменять уши местами?',
        answer: 'Нет. Выберите левый или правый канал в каждой из двух строк блока «Речь» или в панели, которая открывается из переключателя режимов.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Gespräch von Angesicht zu Angesicht in zwei Sprachen',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'Diese Anleitung richtet die Sokuji-Desktop-App für zwei Personen an einem Computer ein, die verschiedene Sprachen sprechen: ein Besuch am Empfang, eine Besprechung am selben Tisch, ein Gespräch mit Verwandten. Sie teilen sich ein Mikrofon und tragen jeweils einen Ohrhörer. Was Sie sagen, wird der anderen Person in ihrer Sprache vorgelesen, was sie sagt, wird Ihnen in Ihrer Sprache vorgelesen, und beide Seiten erscheinen als Untertitel. Es wird nichts in eine Meeting-App gesendet, und Sie brauchen kein virtuelles Audiokabel. Verwendet wird KizunaAI (Unterstützt von Soniox; Gespräche von Angesicht zu Angesicht brauchen die Soniox-Engine). Die Screenshots stammen aus Sokuji 0.44.0 unter Windows: Ich spreche Chinesisch, die andere Person spricht Englisch, und wir teilen uns ein Paar Ohrhörer, meinen im linken Ohr und ihren im rechten.',
  },
  steps: [
    {
      title: 'Sokuji mit KizunaAI einrichten',
      content:
        'Falls noch nicht geschehen, folgen Sie dem <a href="/docs/kizuna-ai/quick-start">KizunaAI-Schnellstart</a>, um Sokuji zu installieren und sich anzumelden.',
    },
    {
      title: '„Gespräch von Angesicht zu Angesicht“ wählen',
      content:
        'Wählen Sie in der Einrichtung <strong>Gespräch von Angesicht zu Angesicht</strong>. Damit wechselt Sokuji in den Modus <strong>Beides</strong> mit dem Gegenüber neben Ihnen und liest beide Übersetzungen vor. Wenn Sie die Einrichtung bereits abgeschlossen haben, folgen Sie stattdessen dem nächsten Schritt.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Die andere Person neben sich einstellen',
      content:
        'Klicken Sie im Modusschalter unten links im Hauptfenster auf <strong>Beides</strong>; wenn sich sein Panel nicht öffnet, klicken Sie erneut auf „Beides“. Wählen Sie unter <strong>Wo ist das Gegenüber?</strong> die Option <strong>Neben mir</strong>. Der Modusschalter trägt jetzt die Markierung <strong>Vor Ort</strong>, und das Panel zeigt, was ein Gespräch von Angesicht zu Angesicht verwendet: Ihr Mikrofon und wo jede Übersetzung abgespielt wird.',
      screenshot: '02-other-side.png',
      tip: '„Audio des Gegenübers“ ist nicht nötig: Die andere Person spricht in Ihr Mikrofon, und Sokuji nimmt kein Systemaudio auf. „Wo ist das Gegenüber?“ lässt sich nicht ändern, während eine Sitzung läuft.',
    },
    {
      title: 'Die beiden Sprachen einstellen',
      content:
        'Stellen Sie in den Einstellungen unter <strong>Übersetzungssprachen</strong> bei <strong>Ich spreche</strong> Ihre Sprache ein und bei <strong>sie hören</strong> die der anderen Person. Die Zeile darunter zeigt es umgekehrt. Hier: Ich spreche Chinesisch, sie hören Englisch, und die Zeile lautet „Sie sprechen Englisch → ich höre Chinesisch“.',
      tip: 'Lassen Sie „Ich spreche“ nicht auf „Automatische Erkennung“. Im Modus „Beides“ muss Sokuji Ihre Sprache kennen, um die andere Person in sie zu übersetzen, und „Starten“ bleibt deaktiviert, bis Sie eine auswählen.',
    },
    {
      title: 'Ein Mikrofon für Sie beide wählen',
      content:
        'Wählen Sie unter <strong>Mikrofon</strong> ein Mikrofon, das Sie beide hört, etwa das eingebaute Mikrofon eines Laptops, der zwischen Ihnen steht, oder ein Tischmikrofon. Das Mikrofon eines Headsets hört nur die Person, die es trägt.',
    },
    {
      title: 'Ohrhörer einsetzen und die Ohren einstellen',
      content:
        'Sie tragen den linken Ohrhörer, die andere Person den rechten. Im Bereich <strong>Sprache</strong> spielt <strong>Übersetzung, die das Gegenüber hört</strong> Ihre Übersetzung im Ohr der anderen Person ab (<strong>rechter Kanal</strong>), und <strong>Übersetzung, die ich höre</strong> spielt deren Übersetzung in Ihrem Ohr ab (<strong>linker Kanal</strong>), beide auf dem Standard-Wiedergabegerät. Die Wiedergabetaste neben jeder Zeile spielt einen kurzen Ton in diesem Ohr ab. Um die Ohren zu tauschen, wählen Sie in jeder Zeile den anderen Kanal; damit jeder von Ihnen ein eigenes Headset nutzt, wählen Sie in jeder Zeile ein Gerät. Ohren und Geräte lassen sich auch während einer Sitzung ändern.',
      screenshot: '03-speech.png',
      tip: 'Jeder einen Ohrhörer, niemals ein Lautsprecher: Über einen Lautsprecher nimmt das Mikrofon die Übersetzung auf und übersetzt sie erneut. „Ich höre es auch“ und „Durchleitung“ werden bei einem Gespräch von Angesicht zu Angesicht nicht angezeigt.',
    },
    {
      title: 'Die beiden Stimmen wählen',
      content:
        'Klicken Sie unter einer der beiden Zeilen auf den Namen der Stimme, oder öffnen Sie <strong>Einstellungen → Erweitert → Anbieter</strong>. <strong>Stimme, die das Gegenüber hört</strong> ist die Stimme, in der die andere Person Ihre Übersetzung hört: eine vordefinierte Stimme oder Ihre eigene geklonte Stimme. <strong>Stimme, die ich höre</strong> liest Ihnen die Übersetzung der anderen Person vor, mit einer der vordefinierten Stimmen. Standardmäßig sind es zwei verschiedene Stimmen. Mehr dazu in der <a href="/docs/kizuna-ai/voices">Anleitung zu Stimmen</a>.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Starten und abwechselnd sprechen',
      content:
        'Klicken Sie auf <strong>Starten</strong> und sprechen Sie abwechselnd. Jeder Satz erscheint mit seiner Übersetzung und wird ins Ohr der zuhörenden Person vorgelesen: Ihre Sätze ins Ohr der anderen Person, deren Sätze in Ihres. Die Leiste über „Starten“ zeigt für jede Person Ohr, Sprache und Gerät, hier <strong>L Chinesisch · ich</strong> und <strong>R Englisch · Gegenüber</strong>, und jede übersetzte Zeile trägt eine Markierung <strong>L</strong> oder <strong>R</strong> für das Ohr, in dem sie abgespielt wird. Eine Zeile mit einem durchgestrichenen Lautsprecher wird nicht abgespielt: Ihre Übersetzung ist in der eigenen Sprache der sprechenden Person herausgekommen, was passieren kann, wenn jemand beide Sprachen mischt, daher wird sie niemandem vorgelesen.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Sprechen Sie abwechselnd und machen Sie am Ende jedes Satzes eine kurze Pause. Wenn Sie beide gleichzeitig sprechen, hört ein einziges Mikrofon Sie vermischt.',
      'Bleiben Sie bei Ihrer eigenen Sprache. Ein Satz in der Sprache der anderen Person kann in Ihre eigene Sprache übersetzt herauskommen und wird dann niemandem vorgelesen.',
      '„Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“ sind festgelegt, sobald eine Sitzung läuft. Beenden Sie die Sitzung, um sie zu ändern.',
      'Beenden Sie die Sitzung, wenn das Gespräch vorbei ist: Eine laufende Sitzung verbraucht weiter Ihr Guthaben.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Können wir nur Untertitel verwenden?',
        answer:
          'Ja. Wählen Sie in der Einrichtung „Gespräch von Angesicht zu Angesicht, nur Untertitel“, oder schalten Sie vor dem Start im Bereich „Sprache“ „Übersetzung, die das Gegenüber hört“ und „Übersetzung, die ich höre“ aus. Dann lesen Sie beide den Bildschirm, und in der Desktop-App kann die andere Person über „Untertitel teilen“ auch auf ihrem Smartphone mitlesen. Sie können auch nur eine der beiden ausschalten, zum Beispiel wenn die andere Person lieber liest.',
      },
      {
        question: 'Was kostet ein Gespräch von Angesicht zu Angesicht?',
        answer:
          'Es läuft immer als eine gemeinsame Sitzung: »Gemeinsame Sitzung im Modus „Beides“« ist fest auf „Aktiviert“ gestellt. Mit Stimme werden beide Hälften gesprochen, Ihre Übersetzung und die der anderen Person, daher kostet es pro Minute mehr als reine Untertitel und braucht zum Starten ein höheres Guthaben. Details finden Sie in der Anleitung zu Guthaben und Kosten.',
      },
      {
        question: 'Brauche ich dafür VB-CABLE?',
        answer: 'Nein. Bei einem Gespräch von Angesicht zu Angesicht werden beide Übersetzungen in Ihren Ohrhörern abgespielt, und nichts wird in eine Meeting-App gesendet.',
      },
      {
        question: 'Welche Anbieter unterstützen Gespräche von Angesicht zu Angesicht?',
        answer: 'KizunaAI mit der Soniox-Engine und Soniox mit Ihrem eigenen API-Schlüssel. Andere Anbieter, auch die Qwen-Engine, können nicht zwei Personen an einem Mikrofon übersetzen.',
      },
      {
        question: 'Gibt es eine Schaltfläche, um die Ohren zu tauschen?',
        answer: 'Nein. Wählen Sie in jeder der beiden Zeilen des Bereichs „Sprache“ den linken oder rechten Kanal, oder im Panel, das sich über den Modusschalter öffnet.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Розмова віч-на-віч двома мовами',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'Цей посібник налаштовує настільний застосунок Sokuji для двох людей за одним комп’ютером, які говорять різними мовами: відвідувач біля стійки реєстрації, зустріч за одним столом, розмова з родичем. Ви користуєтеся одним мікрофоном, і кожен має один навушник. Те, що кажете ви, зачитується співрозмовнику його мовою, те, що каже він, зачитується вам вашою, а мовлення обох сторін показується у вигляді субтитрів. У застосунок для зустрічей нічого не надсилається, і віртуальний аудіокабель не потрібен. Використовується KizunaAI (на базі Soniox; розмова віч-на-віч потребує рушія Soniox). Знімки екрана зроблено в Sokuji 0.44.0 на Windows: я говорю китайською, співрозмовник — англійською, і ми ділимо одну пару навушників: мій у лівому вусі, його — у правому.',
  },
  steps: [
    {
      title: 'Налаштуйте Sokuji з KizunaAI',
      content:
        'Якщо ви ще цього не зробили, дотримуйтеся посібника <a href="/docs/kizuna-ai/quick-start">«Швидкий старт із KizunaAI»</a>, щоб установити Sokuji та увійти в обліковий запис.',
    },
    {
      title: 'Виберіть «Розмова віч-на-віч»',
      content:
        'У майстрі налаштування виберіть <strong>Розмова віч-на-віч</strong>. Sokuji переходить у режим <strong>Обидва</strong>, де співрозмовник поруч із вами, і зачитує вголос обидва переклади. Якщо ви вже завершили налаштування, натомість виконайте наступний крок.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Укажіть, що співрозмовник поруч із вами',
      content:
        'Натисніть <strong>Обидва</strong> в перемикачі режимів у лівому нижньому куті головного вікна; якщо його панель не відкрилася, натисніть «Обидва» ще раз. У розділі <strong>Де співрозмовник</strong> виберіть <strong>Поруч зі мною</strong>. Тепер на перемикачі режимів є позначка <strong>Віч-на-віч</strong>, а панель показує, що використовується в розмові віч-на-віч: ваш мікрофон і де відтворюється кожен переклад.',
      screenshot: '02-other-side.png',
      tip: '«Аудіо співрозмовника» не потрібне: співрозмовник говорить у ваш мікрофон, і Sokuji не захоплює системний звук. «Де співрозмовник» не можна змінити під час сеансу.',
    },
    {
      title: 'Задайте дві мови',
      content:
        'У налаштуваннях, у розділі <strong>Мови перекладу</strong>, укажіть у полі <strong>Я говорю</strong> свою мову, а в полі <strong>вони чують</strong> — мову співрозмовника. Рядок під полями показує зворотний напрямок. Тут: Я говорю китайською, вони чують англійською, а рядок показує «Вони говорять Англійська → я чую Китайська».',
      tip: 'Не залишайте в полі «Я говорю» значення «Автовиявлення». У режимі «Обидва» Sokuji має знати вашу мову, щоб перекладати нею співрозмовника, і кнопка «Почати» залишається недоступною, доки ви її не виберете.',
    },
    {
      title: 'Виберіть один мікрофон для вас обох',
      content:
        'У розділі <strong>Мікрофон</strong> виберіть мікрофон, який чує вас обох, наприклад вбудований мікрофон ноутбука, що стоїть між вами, або настільний мікрофон. Мікрофон гарнітури чує лише того, хто її носить.',
    },
    {
      title: 'Вставте навушники й налаштуйте ліве та праве вухо',
      content:
        'Ви носите лівий навушник, співрозмовник — правий. У блоці <strong>Мовлення</strong> <strong>Переклад, який чує співрозмовник</strong> відтворює ваш переклад у вусі співрозмовника (<strong>правий канал</strong>), а <strong>Переклад, який чую я</strong> — його переклад у вашому (<strong>лівий канал</strong>), обидва на пристрої відтворення за замовчуванням. Кнопка відтворення біля кожного рядка відтворює короткий сигнал у відповідному вусі. Щоб поміняти вуха, виберіть інший канал у кожному рядку; щоб кожен із вас мав власну гарнітуру, виберіть пристрій у кожному рядку. Вуха й пристрої можна змінювати й під час сеансу.',
      screenshot: '03-speech.png',
      tip: 'По одному навушнику кожному, ніколи не динамік: через динамік мікрофон уловлює переклад і перекладає його знову. У розмові віч-на-віч «Я теж чую» і «Passthrough» не показуються.',
    },
    {
      title: 'Виберіть два голоси',
      content:
        'Натисніть назву голосу під будь-яким із рядків або відкрийте <strong>Налаштування → Розширений → Постачальник</strong>. <strong>Голос, який чує співрозмовник</strong> — це голос, яким співрозмовник чує ваш переклад: готовий голос або ваш власний клонований голос. <strong>Голос, який чую я</strong> зачитує вам переклад співрозмовника, з-поміж готових голосів. За замовчуванням це два різні голоси. Докладніше — у <a href="/docs/kizuna-ai/voices">посібнику про голоси</a>.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Почніть і говоріть по черзі',
      content:
        'Натисніть <strong>Почати</strong> й говоріть по черзі. Кожне речення з’являється разом із перекладом і зачитується у вухо слухача: ваше — співрозмовнику, його — вам. Смуга над кнопкою «Почати» показує вухо, мову й пристрій кожного з вас, тут <strong>L Китайська · я</strong> і <strong>R Англійська · співрозмовник</strong>, а кожен перекладений рядок має позначку <strong>L</strong> або <strong>R</strong> — вухо, у якому він відтворюється. Рядок, позначений перекресленим динаміком, не відтворюється: його переклад вийшов мовою самого мовця (таке буває, коли хтось змішує обидві мови), тож його не зачитують нікому.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Говоріть по черзі й робіть паузу в кінці кожного речення. Коли ви говорите одночасно, один мікрофон чує ваші голоси змішаними.',
      'Говоріть своєю мовою. Речення мовою співрозмовника може бути перекладене вашою власною мовою, і тоді його не зачитують нікому.',
      '«Переклад, який чує співрозмовник» і «Переклад, який чую я» фіксуються, щойно запущено сеанс. Щоб змінити їх, зупиніть сеанс.',
      'Зупиняйте сеанс, коли розмову закінчено: запущений сеанс і далі витрачає баланс.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи можна користуватися лише субтитрами?',
        answer:
          'Так. Виберіть у майстрі налаштування «Розмова віч-на-віч, лише субтитри» або перед початком вимкніть «Переклад, який чує співрозмовник» і «Переклад, який чую я» в блоці «Мовлення». Тоді ви обоє читаєте екран, а в настільному застосунку співрозмовник може також стежити за розмовою на своєму телефоні через «Поділитися субтитрами». Можна також вимкнути лише один із двох, наприклад коли співрозмовник волів би читати.',
      },
      {
        question: 'Скільки коштує розмова віч-на-віч?',
        answer:
          'Вона завжди працює як один спільний сеанс: «Спільний сеанс у режимі «Обидва»» зафіксовано в стані «Увімкнено». З голосом озвучуються обидві половини — ваш переклад і переклад співрозмовника, тому це коштує дорожче за хвилину, ніж самі лише субтитри, і для початку сеансу потрібен більший баланс. Докладніше — у посібнику про баланс і витрати.',
      },
      {
        question: 'Чи потрібен для цього VB-CABLE?',
        answer: 'Ні. Розмова віч-на-віч відтворює обидва переклади у ваших навушниках і нічого не надсилає в застосунок для зустрічей.',
      },
      {
        question: 'Які постачальники підтримують розмову віч-на-віч?',
        answer: 'KizunaAI із рушієм Soniox, а також Soniox із вашим власним API-ключем. Інші постачальники, зокрема рушій Qwen, не можуть перекладати двох людей біля одного мікрофона.',
      },
      {
        question: 'Чи є кнопка, щоб поміняти вуха?',
        answer: 'Ні. Виберіть лівий або правий канал у кожному з двох рядків блоку «Мовлення» або в панелі, що відкривається з перемикача режимів.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Conversazione faccia a faccia in due lingue',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'Questa guida configura l’app desktop di Sokuji per due persone davanti allo stesso computer che parlano lingue diverse: un visitatore alla reception, una riunione attorno a un tavolo, una conversazione con un parente. Condividete un solo microfono e indossate un auricolare ciascuno. Ciò che dici viene letto all’altra persona nella sua lingua, ciò che dice lei viene letto a te nella tua, ed entrambe le parti compaiono come sottotitoli. A nessuna app per riunioni viene inviato nulla, e non ti serve un cavo audio virtuale. Usa KizunaAI (Con tecnologia Soniox; la conversazione faccia a faccia richiede il motore Soniox). Le schermate provengono da Sokuji 0.44.0 su Windows: io parlo cinese, l’altra persona parla inglese, e condividiamo un paio di auricolari, il mio nell’orecchio sinistro e il suo nel destro.',
  },
  steps: [
    {
      title: 'Configura Sokuji con KizunaAI',
      content:
        'Se non l’hai ancora fatto, segui la <a href="/docs/kizuna-ai/quick-start">guida rapida a KizunaAI</a> per installare Sokuji e accedere.',
    },
    {
      title: 'Scegli “Conversazione faccia a faccia”',
      content:
        'Nella configurazione guidata, scegli <strong>Conversazione faccia a faccia</strong>. Sokuji passa in modalità <strong>Entrambi</strong> con l’altra parte accanto a te, e legge ad alta voce entrambe le traduzioni. Se hai già completato la configurazione, segui invece il passaggio successivo.',
      screenshot: '01-scenario.png',
    },
    {
      title: 'Indica che l’altra persona è accanto a te',
      content:
        'Fai clic su <strong>Entrambi</strong> nel selettore di modalità in basso a sinistra nella finestra principale; se il suo pannello non si apre, fai di nuovo clic su Entrambi. In <strong>Dov\'è l\'altro</strong>, scegli <strong>Accanto a me</strong>. Il selettore di modalità ora porta l’etichetta <strong>Faccia a faccia</strong>, e il pannello mostra cosa usa il faccia a faccia: il tuo microfono e dove viene riprodotta ciascuna traduzione.',
      screenshot: '02-other-side.png',
      tip: 'Audio dell\'altro non serve: l’altra persona parla nel tuo microfono, e Sokuji non acquisisce l’audio di sistema. Dov\'è l\'altro non si può cambiare mentre una sessione è attiva.',
    },
    {
      title: 'Imposta le due lingue',
      content:
        'In Impostazioni, sotto <strong>Lingue di Traduzione</strong>, imposta <strong>Io parlo</strong> sulla tua lingua e <strong>loro sentono</strong> su quella dell’altra persona. La riga sottostante indica la direzione inversa. Qui: Io parlo cinese, loro sentono inglese, e la riga dice «Loro parlano Inglese → io sento Cinese».',
      tip: 'Non lasciare Io parlo su Rilevamento Automatico. In modalità Entrambi, Sokuji deve conoscere la tua lingua per tradurvi l’altra persona, e Avvia resta disattivato finché non ne scegli una.',
    },
    {
      title: 'Scegli un solo microfono per entrambi',
      content:
        'In <strong>Microfono</strong>, scegli un microfono che vi senta entrambi, per esempio il microfono integrato di un portatile messo tra voi, oppure un microfono da tavolo. Il microfono di una cuffia sente solo la persona che la indossa.',
    },
    {
      title: 'Indossa gli auricolari e assegna le orecchie',
      content:
        'Tu indossi l’auricolare sinistro, l’altra persona quello destro. Nella sezione <strong>Voce</strong>, <strong>Traduzione che sente l\'altra parte</strong> riproduce la tua traduzione nell’orecchio dell’altra persona (<strong>canale destro</strong>), e <strong>Traduzione che sento io</strong> riproduce la sua nel tuo (<strong>canale sinistro</strong>), entrambe sul dispositivo di riproduzione predefinito. Il pulsante di riproduzione accanto a ogni riga emette un breve suono in quell’orecchio. Per scambiare le orecchie, scegli l’altro canale in ogni riga; per dare a ciascuno di voi una cuffia propria, scegli un dispositivo in ogni riga. Orecchie e dispositivi si possono cambiare anche durante una sessione.',
      screenshot: '03-speech.png',
      tip: 'Un auricolare a testa, mai un altoparlante: con un altoparlante il microfono capta la traduzione e la traduce di nuovo. Lo sento anche io e Passthrough non vengono mostrati nel faccia a faccia.',
    },
    {
      title: 'Scegli le due voci',
      content:
        'Fai clic sul nome della voce sotto una delle due righe, oppure apri <strong>Impostazioni → Avanzato → Fornitore</strong>. <strong>Voce che sente l\'altra parte</strong> è la voce con cui l’altra persona sente la tua traduzione: una voce predefinita o la tua voce clonata. <strong>Voce che sento io</strong> ti legge la traduzione dell’altra persona, con una delle voci predefinite. Per impostazione predefinita le due voci sono diverse. Trovi altro nella <a href="/docs/kizuna-ai/voices">guida alle voci</a>.',
      screenshot: '04-voices.png',
    },
    {
      title: 'Avvia e parlate a turno',
      content:
        'Fai clic su <strong>Avvia</strong> e parlate a turno. Ogni frase compare con la sua traduzione e viene letta nell’orecchio di chi ascolta: la tua in quello dell’altra persona, la sua nel tuo. La striscia sopra Avvia mostra l’orecchio, la lingua e il dispositivo di ciascuno, qui <strong>L Cinese · io</strong> e <strong>R Inglese · l\'altro</strong>, e ogni riga tradotta porta un’etichetta <strong>L</strong> o <strong>R</strong> per l’orecchio in cui viene riprodotta. Una riga contrassegnata da un altoparlante barrato non viene riprodotta: la sua traduzione è uscita nella lingua stessa di chi parla, cosa che può succedere quando qualcuno mescola le due lingue, quindi non viene letta a nessuno.',
      screenshot: '05-conversation.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Parlate a turno e fate una pausa alla fine di ogni frase. Quando parlate insieme, un solo microfono vi sente mescolati.',
      'Resta nella tua lingua. Una frase nella lingua dell’altra persona può uscire tradotta nella tua, e allora non viene letta a nessuno.',
      'Traduzione che sente l\'altra parte e Traduzione che sento io restano fisse una volta avviata la sessione. Interrompi la sessione per cambiarle.',
      'Interrompi la sessione quando la conversazione finisce: una sessione attiva continua a consumare il saldo.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Possiamo usare solo i sottotitoli?',
        answer:
          'Sì. Scegli Conversazione faccia a faccia, solo sottotitoli nella configurazione guidata, oppure disattiva Traduzione che sente l\'altra parte e Traduzione che sento io nella sezione Voce prima di avviare. Allora leggete entrambi lo schermo, e nell’app desktop l’altra persona può seguire anche sul proprio telefono tramite la condivisione dei sottotitoli. Puoi anche disattivarne solo una, per esempio quando l’altra persona preferisce leggere.',
      },
      {
        question: 'Quanto costa una conversazione faccia a faccia?',
        answer:
          'Viene sempre eseguita come una sola sessione condivisa: Sessione condivisa in modalità Entrambi è fissata su Abilitato. Con la voce vengono pronunciate entrambe le metà, la tua traduzione e quella dell’altra persona, quindi costa di più al minuto rispetto ai soli sottotitoli e per avviarla serve un saldo più alto. Trovi i dettagli nella guida su saldo e costi.',
      },
      {
        question: 'Per questo mi serve VB-CABLE?',
        answer: 'No. Il faccia a faccia riproduce entrambe le traduzioni nei vostri auricolari e non invia nulla a un’app per riunioni.',
      },
      {
        question: 'Quali fornitori supportano il faccia a faccia?',
        answer: 'KizunaAI con il suo motore Soniox, e Soniox con la tua chiave API. Gli altri fornitori, compreso il motore Qwen, non possono tradurre due persone con un solo microfono.',
      },
      {
        question: 'C’è un pulsante per scambiare le orecchie?',
        answer: 'No. Scegli il canale sinistro o destro in ciascuna delle due righe della sezione Voce, oppure nel pannello che si apre dal selettore di modalità.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaFaceToFaceTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-face-to-face" />;
}
